import { AxiosResponseAction, Dispatch, Store } from '../../reducers'
import { FileAction, IFileDto, IOverviewItemDto } from '../../config/fileConfig'
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import {
    DocumentOverviewItemDto,
    DocumentSortOrder,
    IDocumentOverviewItemDto,
    IDocumentStackItemDto,
    IRootOverviewItemDto,
    isFolder,
} from '../../config/documentConfig'
import { DisbursementOverviewDto, FixedPriceOverviewDto, TravelOverviewDto } from '../../config/registrationConfig'
import { useDispatch, useSelector } from 'react-redux'
import { LegalAidCertificate } from '../../components/Form/LegalAidCertificate/LegalAidCertificateForm'
import { useEffect, useMemo, useState } from 'react'
import { DriveItem, Message } from '@microsoft/microsoft-graph-types'
import { NoteOverviewDto } from '../../config/noteConfig'
import { FilePartyAction, MainDto } from '../../config/filePartyConfig'
import useAxios from '../../hooks/useAxios'
import {
    DataPromise,
    DataPromiseState,
    Destination,
    getDataEmptyState,
    getDataPromiseState,
    IGraphArrayPayload,
    IGraphArrayResponse,
    IGraphResponse,
    shouldDispatchLoad,
    SpringBootError,
} from '../../context/AxiosContext'
import { FilePageProps } from './useFilePages'
import { ThunkActionDispatch } from 'redux-thunk/src/types'
import { FileDocumentAction, FileDocumentChildrenMap, FileDocumentParentMap, IFileDocumentResponse } from '../../config/fileDocumentConfig'
import { ThunkAction } from 'redux-thunk'
import { DocumentNodeType } from '../../config/documentConfig'
import { driveItemOverviewProperties, mapDriveItemToOverviewItemDto } from '../../helpers/documentHelper'
import { ActionType } from 'redux-promise-middleware'
import { IFileState } from '../../reducers/fileReducer'
import { IFileDocumentState } from '../../reducers/fileDocumentReducer'
import { IFilePartyState } from '../../reducers/filePartyReducer'
import useModules from '../module/useModules'
import { ModuleType } from '../../context/GlobalContext'
import { FILE_REFERENCE_NUMBER_PROP } from '../../helpers/referenceNumberHelper'
import { DocketProceedingDto } from '../../components/Form/Docket/DocketProceedingForm'

type FileDataValueTypes = {
    current: IFileDto
    document: IRootOverviewItemDto
    email: IGraphArrayPayload<Message>
    legalAidCertificate: LegalAidCertificate[]
    docketProceeding: DocketProceedingDto[]
    note: NoteOverviewDto[]
    disbursement: DisbursementOverviewDto[]
    fixedPrice: FixedPriceOverviewDto[]
    party: MainDto[]
    travel: TravelOverviewDto[]
}

export type FileData = {
    [K in keyof FileDataValueTypes]: DataPromise<FileDataValueTypes[K]>
}

export type FileDataAction = (...args) => any
export type FileDataActions = { [K in keyof Omit<FileData, 'current'>]: FileDataAction }

function childDocumentsResponse(
    axios: AxiosInstance,
    response: AxiosResponse,
    data: DriveItem[],
    sortOrder: DocumentSortOrder,
    stack: IDocumentStackItemDto[],
    type: FileDocumentAction,
): ThunkAction<void, null, null, IFileDocumentResponse> {
    return (dispatch: Dispatch) => {
        // Download next pages if there is more data to consume.
        if (response.data?.['@odata.nextLink'] !== undefined) {
            return axios
                .get(response.data['@odata.nextLink'], {
                    baseURL: Destination.Documents,
                })
                .then((response: IGraphArrayResponse<DriveItem>) =>
                    dispatch(childDocumentsResponse(axios, response, [...data, ...response.data.value], sortOrder, stack, type)),
                )
        }

        const payload: AxiosResponse<DocumentOverviewItemDto[]> = {
            ...response,
            data: data.map(child => mapDriveItemToOverviewItemDto(child, stack)),
        }

        // Traverse into all children that are a folder.
        payload.data.filter(isFolder).forEach(node =>
            dispatch(
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                getFileChildDocuments(axios, sortOrder, [...stack, { id: node.id, name: node.name }], FileDocumentParentMap.get(type)),
            ),
        )

        payload.data.sort((a: IDocumentOverviewItemDto, b: IDocumentOverviewItemDto) => {
            // Don't sort the folders
            if (a.type === DocumentNodeType.Folder || b.type === DocumentNodeType.Folder) {
                return 0
            }

            switch (sortOrder) {
                case DocumentSortOrder.NameAsc: {
                    return a.name.localeCompare(b.name)
                }
                case DocumentSortOrder.NameDesc: {
                    return b.name.localeCompare(a.name)
                }
                case DocumentSortOrder.ModifiedAsc: {
                    return new Date(a.date).getTime() - new Date(b.date).getTime()
                }
            }

            return new Date(b.date).getTime() - new Date(a.date).getTime()
        })

        // Actually dispatch the payload to Redux.
        return dispatch({ type, payload, stack })
    }
}

function getFileChildDocuments(
    axios: AxiosInstance,
    sortOrder: DocumentSortOrder,
    stack: IDocumentStackItemDto[] = [],
    action: FileDocumentAction = FileDocumentAction.GetOverview,
): ThunkAction<void, null, null, IFileDocumentResponse> {
    if ((stack || []).length === 0) {
        // We expect to never hit this condition, because this call should only be called from a file context.
        return null
    }

    return (dispatch: Dispatch) =>
        axios
            .get(`/items/${[...stack].pop().id}/children`, {
                baseURL: Destination.Documents,
                params: { $select: driveItemOverviewProperties.join(',') },
            })
            .then((response: IGraphArrayResponse<DriveItem>) =>
                dispatch(childDocumentsResponse(axios, response, response.data.value, sortOrder, stack, FileDocumentChildrenMap.get(action))),
            )
            .catch(error => dispatch({ type: `${action}_${ActionType.Rejected}`, payload: error }))
}

// Different actions can be used to store data on a different place in the Redux store.
// This is used in dropdowns that can show documents from a different file than the active one.
function getFileDocuments(
    axios: AxiosInstance,
    file: IOverviewItemDto,
    sortOrder: DocumentSortOrder,
    action: FileDocumentAction = FileDocumentAction.GetOverview,
): ThunkAction<void, null, null, AxiosResponseAction<IRootOverviewItemDto>> {
    return (dispatch: Dispatch) => {
        dispatch({ type: `${action}_${ActionType.Pending}` })
        axios
            .get(file.documentRootId ? `/items/${file.documentRootId}` : `/root:/${file.reference}`, {
                baseURL: Destination.Documents,
                headers: { SuppressErrors: 'true', 'If-None-Match': null },
                params: {
                    $expand: 'children',
                    $select: driveItemOverviewProperties.join(','),
                },
            })
            .then((response: IGraphResponse<DriveItem>) => {
                const payload: AxiosResponse<IRootOverviewItemDto> = {
                    ...response,
                    data: mapDriveItemToOverviewItemDto(response.data) as IRootOverviewItemDto,
                }
                dispatch({ type: `${action}_${ActionType.Fulfilled}`, payload })
                dispatch(
                    childDocumentsResponse(
                        axios,
                        response,
                        response.data.children,
                        sortOrder,
                        [{ id: payload.data.id, name: payload.data.name }],
                        FileDocumentChildrenMap.get(action),
                    ),
                )
            })
            .catch(error => dispatch({ type: `${action}_${ActionType.Rejected}`, payload: error }))
    }
}

function axiosPromiseRequestHandler<D, S = undefined, E = SpringBootError>(
    axios: AxiosInstance,
    state: DataPromise<D, S, E>,
    setter: (state: DataPromise<D, S, E>) => void,
    config: AxiosRequestConfig,
): () => void {
    setter(getDataPromiseState<D, S, E>(DataPromiseState.Loading, state.data))
    axios
        .request<D>(config)
        .then(result => setter(getDataPromiseState<D, S, E>(DataPromiseState.Loaded, result.data)))
        .catch(() => setter(getDataPromiseState<D, S, E>(DataPromiseState.Rejected)))

    return () => setter(getDataEmptyState<D, S, E>())
}

export default function useFileData(fileId?: string, activePage?: FilePageProps): { file: FileData; getActivePageAction: () => FileDataAction } {
    const axios = useAxios()
    const dispatch = useDispatch<ThunkActionDispatch<any>>()
    const modules = useModules()

    // The base data we contain in this file
    const fileState = useSelector<Store, IFileState>(state => state.files)
    const documentState = useSelector<Store, IFileDocumentState>(state => state.fileDocuments)
    const partyState = useSelector<Store, IFilePartyState>(state => state.fileParties)
    const reduxData = {
        current: fileState.current,
        document: documentState.overview,
        email: fileState.emails,
        note: fileState.notes,
        party: partyState.overview,
    }

    const [legalAidCertificates, setLegalAidCertificates] = useState(getDataEmptyState<LegalAidCertificate[]>(undefined, []))
    const [docketProceedings, setDocketProceedings] = useState(getDataEmptyState<DocketProceedingDto[]>(undefined, []))
    const [disbursements, setDisbursements] = useState(getDataEmptyState<DisbursementOverviewDto[]>(undefined, []))
    const [fixedPrices, setFixedPrices] = useState(getDataEmptyState<FixedPriceOverviewDto[]>(undefined, []))
    const [travels, setTravels] = useState(getDataEmptyState<TravelOverviewDto[]>(undefined, []))

    const data: FileData = useMemo(
        () => ({
            ...reduxData,
            legalAidCertificate: legalAidCertificates,
            docketProceeding: docketProceedings,
            disbursement: disbursements,
            fixedPrice: fixedPrices,
            travel: travels,
        }),
        [reduxData, legalAidCertificates, docketProceedings, disbursements, fixedPrices, travels],
    )
    const currentFile: IFileDto = data?.current.data

    const actions: FileDataActions = {
        document: (sortOrder: DocumentSortOrder = DocumentSortOrder.ModifiedDesc, stack?: IDocumentStackItemDto[]) =>
            fileId && dispatch(stack ? getFileChildDocuments(axios, sortOrder, stack) : getFileDocuments(axios, currentFile, sortOrder)),
        email: () =>
            fileId &&
            currentFile.reference &&
            dispatch({
                type: FileAction.GetEmails,
                payload: axios
                    .get('/email/replication/parent-folder/id', { params: { references: [currentFile.reference] } })
                    .then((response: AxiosResponse<{ [key: string]: string }>) => response.data[currentFile.reference] || 'inbox')
                    .then((replicationId: string) =>
                        axios.get(`/mailFolders('${replicationId}')/messages`, {
                            baseURL: Destination.ReplicationEmail,
                            headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                            params: {
                                $select:
                                    'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                                $filter: `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${currentFile.reference}]'))`,
                                $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
                                $top: 1000,
                            },
                        }),
                    ),
            }),
        legalAidCertificate: () => {
            return fileId && modules.has(ModuleType.LegalAid)
                ? axiosPromiseRequestHandler<LegalAidCertificate[]>(axios, legalAidCertificates, setLegalAidCertificates, {
                      url: `/file/${fileId}/legal-aid-certificate`,
                      method: 'GET',
                  })
                : undefined
        },
        docketProceeding: () => {
            return fileId && modules.has(ModuleType.Docket)
                ? axiosPromiseRequestHandler<DocketProceedingDto[]>(axios, docketProceedings, setDocketProceedings, {
                      url: `/file/${fileId}/docket-proceeding`,
                      method: 'GET',
                  })
                : undefined
        },
        party: () =>
            fileId &&
            dispatch({
                type: FilePartyAction.GetOverview,
                payload: axios.get(`/file/${fileId}/party`),
            }),
        note: () =>
            fileId &&
            dispatch({
                type: FileAction.GetNotes,
                payload: axios.get(`/file/${fileId}/note`),
            }),
        disbursement: () => {
            return fileId && modules.has(ModuleType.Invoices)
                ? axiosPromiseRequestHandler<DisbursementOverviewDto[]>(axios, disbursements, setDisbursements, {
                      url: `/file/${fileId}/disbursement`,
                      method: 'GET',
                  })
                : undefined
        },
        fixedPrice: () => {
            return fileId && modules.has(ModuleType.Invoices)
                ? axiosPromiseRequestHandler<FixedPriceOverviewDto[]>(axios, fixedPrices, setFixedPrices, {
                      url: `/file/${fileId}/fixed-price`,
                      method: 'GET',
                  })
                : undefined
        },
        travel: () => {
            return fileId && modules.has(ModuleType.Invoices)
                ? axiosPromiseRequestHandler<TravelOverviewDto[]>(axios, travels, setTravels, {
                      url: `/file/${fileId}/travel`,
                      method: 'GET',
                  })
                : undefined
        },
    }

    // Load the main file data.
    useEffect(() => {
        if (fileId && (shouldDispatchLoad(data.current) || fileId !== data.current.data?.id)) {
            dispatch({ type: FileAction.ResetCurrent })
            dispatch({
                type: FileAction.Get,
                payload: axios.get(`/file/${fileId}`),
            })
        }
    }, [data.current.loaded, fileId])

    // Set the active tab and trigger loading the tab data.
    useEffect(() => {
        if (data.current.loaded && activePage) {
            Object.keys(actions)
                // Make sure to first load the active tab before the other content.
                .sort(key => (key === (activePage.dataActionKey || activePage.key) ? 1 : -1))
                .filter(key => shouldDispatchLoad(data[key]))
                .map(key => actions[key])
                .forEach(action => action())
        }
    }, [...Object.values(data).map(entity => entity.data), activePage])

    return {
        file: data,
        getActivePageAction: () => actions[activePage.key],
    }
}
